import { t } from 'i18next'
import { ZodEffects, ZodObject, ZodRawShape, z } from 'zod'

export const billingEmailEditSchema = (
  allowedDomains: string[],
): ZodEffects<ZodObject<ZodRawShape>> =>
  z
    .object({
      billingEmails: z.array(
        z
          .object({
            productId: z.number(),
            isGeneral: z.boolean(),
            useDisplayName: z.boolean(),
            displayName: z
              .string()
              .max(256, {
                message: t('message.max_length', {
                  entity: t('pages.billing.label.display_name'),
                  length: 256,
                }),
              })
              .optional(),
            emails: z.array(
              z.object({
                value: z
                  .string()
                  .min(1, {
                    message: t('message.min_length', {
                      entity: t('pages.billing.label.email'),
                      length: 1,
                    }),
                  })
                  .email({
                    message: t('message.invalidEmail'),
                  })
                  .max(256)
                  .refine(
                    (email) => {
                      if (allowedDomains.length === 0) {
                        return true
                      }
                      const domain = email.split('@')[1]
                      return allowedDomains.includes(domain)
                    },
                    {
                      message: t('pages.billing.message.invalid_domain'),
                    },
                  ),
              }),
            ),
          })
          .superRefine((data, ctx) => {
            if (data.useDisplayName && !data.displayName) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('message.required', {
                  entity: t('pages.billing.label.display_name'),
                }),
                path: ['displayName'],
              })
            }

            // 同一ブロック内でのメールアドレス重複チェック
            const seen = new Map<string, number[]>()
            for (const [index, item] of data.emails.entries()) {
              if (seen.has(item.value)) {
                seen.get(item.value)?.push(index)
              } else {
                seen.set(item.value, [index])
              }
            }
            for (const indices of seen.values()) {
              if (indices.length <= 1) {
                continue
              }
              for (const index of indices) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t('message.duplicate', {
                    entity: t('pages.billing.label.email'),
                  }),
                  path: ['emails', index, 'value'],
                })
              }
            }
          }),
      ),
    })
    // プロダクト個別送付先のプロダクト重複チェック
    .superRefine((data, ctx) => {
      const seen = new Set<number>()
      for (const [index, billingEmail] of data.billingEmails.entries()) {
        if (billingEmail.isGeneral) {
          continue
        }
        if (seen.has(billingEmail.productId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('message.duplicate', {
              entity: t('pages.billing.label.product'),
            }),
            path: ['billingEmails', index, 'productId'],
          })
        }
        seen.add(billingEmail.productId)
      }
    })

export type BillingEmailEditSchema = {
  billingEmails: Array<{
    productId: number
    isGeneral: boolean
    useDisplayName: boolean
    displayName: string
    emails: Array<{ value: string }>
  }>
}
